/*
 * Copyright (C) 2017 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React, {Component} from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {bindActionCreators} from 'redux'
import type {Dispatch} from 'redux'
import {useScope as createI18nScope} from '@canvas/i18n'
import select from '@canvas/obj-select'

import {Text} from '@instructure/ui-text'
import {Spinner} from '@instructure/ui-spinner'
import SyncHistoryItem from '@canvas/blueprint-courses/react/components/SyncHistoryItem'

import actions from '@canvas/blueprint-courses/react/actions'
import propTypes from '@canvas/blueprint-courses/react/propTypes'
import LoadStates from '@canvas/blueprint-courses/react/loadStates'
import type {Course, Migration} from '../types'

const I18n = createI18nScope('blueprint_settingsSyncHistory')
const loadStates = LoadStates as unknown as {
  hasLoaded: (state: string) => boolean
  isLoading: (state: string) => boolean
}

const {func, bool} = PropTypes

interface SyncHistoryProps {
  migrations: Migration[]
  loadHistory: () => void
  isLoadingHistory: boolean
  hasLoadedHistory: boolean
  associations: Course[]
  loadAssociations: () => void
  isLoadingAssociations: boolean
  hasLoadedAssociations: boolean
}

interface SyncHistoryState {
  associations: Record<string, Course>
}

export default class SyncHistory extends Component<SyncHistoryProps, SyncHistoryState> {
  static propTypes = {
    migrations: propTypes.migrationList,
    loadHistory: func.isRequired,
    isLoadingHistory: bool.isRequired,
    hasLoadedHistory: bool.isRequired,
    associations: propTypes.courseList,
    loadAssociations: func.isRequired,
    isLoadingAssociations: bool.isRequired,
    hasLoadedAssociations: bool.isRequired,
  }

  static defaultProps = {
    migrations: [],
    associations: [],
  }

  constructor(props: SyncHistoryProps) {
    super(props)
    this.state = {
      associations: this.mapAssociations(props.associations),
    }
  }

  componentDidMount() {
    if (!this.props.hasLoadedHistory) {
      this.props.loadHistory()
    }
    if (!this.props.hasLoadedAssociations) {
      this.props.loadAssociations()
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: SyncHistoryProps) {
    this.setState({
      associations: this.mapAssociations(nextProps.associations),
    })
  }

  mapAssociations(assocs: Course[] = []) {
    return assocs.reduce<Record<string, Course>>((map, asc) => {
      if (asc.id) {
        map[asc.id] = asc
      }
      return map
    }, {})
  }

  renderLoading() {
    if (this.props.isLoadingHistory || this.props.isLoadingAssociations) {
      const title = I18n.t('Loading Sync History')
      return (
        <div style={{textAlign: 'center'}}>
          <Spinner renderTitle={title} />
          <Text as="p">{title}</Text>
        </div>
      )
    }

    return null
  }

  render() {
    // inject course data into exceptions
    const migrations = this.props.migrations.map(mig => {
      mig.changes.forEach(change => {
        change.exceptions?.forEach(ex =>
          Object.assign(ex, this.state.associations[ex.course_id] || {}),
        )
      })
      return mig
    })

    return (
      <div className="bcs__history">
        {this.renderLoading() ||
          migrations.map(migration => <SyncHistoryItem key={migration.id} migration={migration} />)}
      </div>
    )
  }
}

const connectState = (state: Record<string, unknown>) => {
  const selectedChangeLogId = state.selectedChangeLog as string | null | undefined
  const changeLogs = (state.changeLogs ?? {}) as Record<string, {status: string; data?: Migration}>
  const selectedChange = selectedChangeLogId ? changeLogs[selectedChangeLogId] : null
  const historyState = selectedChange
    ? {
        hasLoadedHistory: loadStates.hasLoaded(selectedChange.status),
        isLoadingHistory: loadStates.isLoading(selectedChange.status),
        migrations: selectedChange.data ? [selectedChange.data] : [],
      }
    : select(state, ['hasLoadedHistory', 'isLoadingHistory', 'migrations'])

  return Object.assign(
    select(state, [
      'hasLoadedAssociations',
      'isLoadingAssociations',
      ['existingAssociations', 'associations'],
    ]),
    historyState,
  )
}
const connectActions = (dispatch: Dispatch) => bindActionCreators(actions, dispatch)
export const ConnectedSyncHistory = connect(
  connectState,
  connectActions,
)(SyncHistory) as unknown as React.ComponentType<Record<string, unknown>>
